import { type ReactNode, type CSSProperties } from 'react';

import { type AuthStatus } from '../types';

import { useDocyrusAuth } from '../hooks/use-docyrus-auth';

const ICON_SIZE = 20;

function DocyrusLogo({ size = ICON_SIZE }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1024 1024"
      width={size}
      height={size}
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0 }}>
      <path
        fill="#dc2626"
        d="M768.11,531.68l-.24.21v101.17c0,26.8-21.79,48.63-48.59,48.63h-101.17c-8.72,0-16.82-2.33-23.88-6.35l-113.69,113.73c7.41,11.02,11.77,24.33,11.77,38.47,0,18.98-7.62,36.17-20.04,48.63-12.42,12.42-29.65,20.04-48.63,20.04s-36.2-7.62-48.63-20.04c-12.42-12.46-20.04-29.65-20.04-48.63,0-37.96,30.75-68.91,68.87-68.91,14.48,0,27.76,4.43,38.78,11.94l113.62-113.62c-4.19-7.1-6.55-15.31-6.55-24.09v-101.17c0-11.12,3.81-21.48,10.19-29.68l-93.72-127.52h-229.96V127.7h246.57v225.88l97.6,132.84c5.56-2.23,11.63-3.36,17.95-3.36h101.17c26.8,0,48.63,21.83,48.63,48.63Z" />
    </svg>
  );
}

function Spinner({ size = ICON_SIZE }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0, animation: 'docyrus-spin 1s linear infinite' }}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path
        d="M12 2a10 10 0 0 1 10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round" />
      <style>{`@keyframes docyrus-spin{to{transform:rotate(360deg)}}`}</style>
    </svg>
  );
}

export interface SignInButtonProps {
  /** Custom label. Defaults to "Sign in with Docyrus" */
  label?: string;
  /** CSS class name for the button */
  className?: string;
  /** Inline styles */
  style?: CSSProperties;
  /** Custom render function. Receives signIn, isLoading and status. */
  children?: (props: {
    signIn: () => void;
    isLoading: boolean;
    status: AuthStatus;
  }) => ReactNode;
  /** Disable the button */
  disabled?: boolean;
}

/**
 * "Sign in with Docyrus" button.
 *
 * In iframe mode: renders nothing (auth is handled by the host).
 * When authenticated: renders nothing.
 *
 * Default look: [Logo] label  — or — [Spinner] label (when loading).
 * Use className, style, or the render-prop children for full customization.
 */
export function SignInButton({
  label = 'Sign in with Docyrus',
  className,
  style,
  children,
  disabled = false
}: SignInButtonProps) {
  const { signIn, status, mode } = useDocyrusAuth();

  const isLoading = status === 'loading';

  // In iframe mode, the host handles sign-in
  if (mode === 'iframe') return null;

  // Already authenticated
  if (status === 'authenticated') return null;

  // Custom render
  if (children) {
    return <>{children({ signIn, isLoading, status })}</>;
  }

  return (
    <button
      type="button"
      onClick={signIn}
      disabled={disabled || isLoading}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        ...style
      }}>
      {isLoading ? <Spinner /> : <DocyrusLogo />}
      <span>{label}</span>
    </button>
  );
}
